import { api } from '../api/vykoClient';
import { ActiveInpatient, DepartmentItem, NurseUser, PatientHandoverItem, WardServicePatientListFilter } from '../types';
import { appLogger } from '../utils/logger';
import { toggleTheme, isDarkMode } from '../utils/theme';
import { BatchAcceptModal } from './BatchAcceptModal';
import { GiveHandoverModal } from './GiveHandoverModal';
import { UserSettingsModal, UserPreferencesConfig } from './UserSettingsModal';
import { userService } from '../services/userService';
import { handoverTrackingService, HandoverAssignmentRecord } from '../services/handoverTrackingService';
import {
  getPatientArrivalStatus,
  renderPatientGenderIcon,
  getPatientDisplayName,
  formatDoctorName,
  resolvePatientGender,
} from '../utils/patientHelper';
import { showToast } from '../utils/toast';

const SPECIALTY_CODE_MAP: Record<string, string> = {
  'ORTHOPAEDICS': 'ORTH',
  'ORTHOPEDICS': 'ORTH',
  'ORTH': 'ORTH',
  'GENERAL MEDICINE': 'MEDI',
  'MEDICINE': 'MEDI',
  'MEDI': 'MEDI',
  'OBSTETRICS & GYNAECOLOGY': 'GYN',
  'OBG': 'GYN',
  'GYN': 'GYN',
  'GYNAECOLOGY': 'GYN',
  'GENERAL SURGERY': 'SUGR',
  'SURGERY': 'SUGR',
  'SUGR': 'SUGR',
  'SURGICAL GASTROENTEROLOGY': 'SUGR',
  'CARDIOLOGY': 'CARD',
  'CARD': 'CARD',
  'NEUROLOGY': 'NEUR',
  'NEURO SURGERY': 'NEUS',
  'PAEDIATRICS': 'PAED',
  'PEDIATRICS': 'PAED',
  'PULMONOLOGY': 'PULM',
  'UROLOGY': 'UROL',
  'NEPHROLOGY': 'NEPH',
  'DERMATOLOGY': 'DERM',
  'OPHTHALMOLOGY': 'OPHT',
  'ENT': 'ENT',
  'OTORHINOLARYNGOLOGY (ENT)': 'ENT',
};

const WARD_CODE_MAP: Record<string, string> = {
  'GENERAL WARD': '21666',
};

const ROOM_CODE_MAP: Record<string, string> = {
  'MALE ORTHO WARD': '15088',
  'FEMALE ORTHO WARD': '15089',
  'FEMALE MEDICAL WARD': '15031',
  'MALE MEDICAL WARD': '15081',
  'OBG WARD': '15106',
  'FEMALE SURGICAL GASTROENTEROLOGY': '22510',
};

export class Dashboard {
  private container: HTMLElement;
  private onLogout: () => void;

  // Handover Queue State (Acknowledge tab)
  private patients: PatientHandoverItem[] = [];
  private totalAcceptedCount = 0;
  private searchQuery = '';
  private isLoading = false;
  private searchDebounceTimer: number | null = null;

  // Preferable Ward Types, Departments & Room Types (Stored in Cloudflare D1)
  private preferredWardTypes: string[] = [];
  private preferredDepartments: string[] = [];
  private preferredRoomTypes: string[] = [];
  private preferredHandoverNurse: NurseUser | null = null;
  private applyUserPreferencesFilter = true;

  // Active Inpatients State (Assign tab)
  private currentTab: 'acknowledge' | 'assign' = 'acknowledge';
  private activePatients: ActiveInpatient[] = [];
  private activePatientsPage = 1;
  private activePatientsTotalPages = 1;
  private activePatientsTotalCount = 0;
  private isLoadingActivePatients = false;
  private departments: DepartmentItem[] = [];

  // Assign tab: global nurse picker
  private assignTargetNurse: NurseUser | null = null;
  private assignNurseSearchQuery = '';
  private assignNurseResults: NurseUser[] = [];
  private isSearchingAssignNurse = false;
  private showAssignNursePicker = false;
  private assignNurseDebounceTimer: number | null = null;

  // Assign tab: multi-select batch assignment
  private selectedAssignPatientKeys: Set<string> = new Set();
  private isBatchAssigning = false;

  // Active Filter States (now in settings, but still used for server queries)
  private activeSpecialtyFilter: string = '';
  private activeWardFilter: string = '';
  private activeRoomFilter: string = '';
  private activeMyPatientFilter: boolean = false;

  constructor(container: HTMLElement, onLogout: () => void) {
    this.container = container;
    this.onLogout = onLogout;

    // Start 45-second heartbeat & 24-hour expiration monitoring
    userService.startHeartbeat(
      (reason) => {
        showToast(reason || 'Your account has been suspended by the administrator.', 'error');
        this.destroy();
        this.onLogout();
      },
      () => {
        showToast('Session expired after 24 hours. Please log in again.', 'warn');
        this.destroy();
        this.onLogout();
      }
    );

    this.loadUserPreferences();
    this.loadHandovers();
    this.preloadDepartments();
  }

  private async loadUserPreferences() {
    if (!api.loginId) return;
    try {
      const prefs = await userService.getUserPreferences(api.loginId);
      this.preferredWardTypes = prefs.preferredWardTypes || [];
      this.preferredDepartments = prefs.preferredDepartments || [];
      this.preferredRoomTypes = prefs.preferredRoomTypes || [];
      this.preferredHandoverNurse = prefs.preferredHandoverNurse || null;
      // Pre-fill assign nurse from preferences
      if (this.preferredHandoverNurse && !this.assignTargetNurse) {
        this.assignTargetNurse = this.preferredHandoverNurse;
      }
      this.render();

      // If user did not set filter, prompt to set filter via preference settings
      if (!this.hasActivePreferences() && !sessionStorage.getItem('vyko_filter_prompt_dismissed')) {
        sessionStorage.setItem('vyko_filter_prompt_dismissed', '1');
        this.openSettingsModal();
      }
    } catch (e) {
      console.warn('Could not load preferences:', e);
    }
  }

  public showFilterSetupPrompt() {
    this.openSettingsModal();
  }

  public hasActivePreferences(): boolean {
    return (
      this.preferredWardTypes.length > 0 ||
      this.preferredDepartments.length > 0 ||
      this.preferredRoomTypes.length > 0
    );
  }

  public doesPatientMatchPreferences(patient: PatientHandoverItem | ActiveInpatient): boolean {
    const hasWardTypes = this.preferredWardTypes.length > 0;
    const hasDepts = this.preferredDepartments.length > 0;
    const hasRoomTypes = this.preferredRoomTypes.length > 0;

    if (!hasWardTypes && !hasDepts && !hasRoomTypes) {
      return true;
    }

    const patientWards = [
      (patient as any).wardTypeName,
      (patient as any).wardName,
      (patient as any).ward,
      (patient as any).wardType,
      (patient as any).wardTypeNo,
    ].filter((v): v is string | number => v !== undefined && v !== null && String(v).trim() !== '')
      .map((v) => String(v).trim().toUpperCase());

    const patientDepts = [
      (patient as any).department,
      (patient as any).dischargedepartmnt,
      (patient as any).departmentName,
      (patient as any).specialtyCode,
      (patient as any).dischargedUnderSpecialty,
      (patient as any).specialtyCodeValue,
    ].filter((v): v is string | number => v !== undefined && v !== null && String(v).trim() !== '')
      .map((v) => String(v).trim().toUpperCase());

    const patientRooms = [
      (patient as any).roomTypeName,
      (patient as any).room,
      (patient as any).roomType,
      (patient as any).roomTypeNo,
      (patient as any).admittedRoomTypeValue,
      (patient as any).bedNumber,
      (patient as any).admittedBedNumber,
    ].filter((v): v is string | number => v !== undefined && v !== null && String(v).trim() !== '')
      .map((v) => String(v).trim().toUpperCase());

    const isMatch = (candidates: string[], preferredList: string[]) => {
      return preferredList.some((pref) => {
        const p = String(pref).trim().toUpperCase();
        if (!p) return false;
        const mappedCode = SPECIALTY_CODE_MAP[p] || WARD_CODE_MAP[p] || ROOM_CODE_MAP[p] || '';
        return candidates.some((cand) => {
          return (
            cand === p ||
            cand.includes(p) ||
            p.includes(cand) ||
            (mappedCode !== '' && (cand === mappedCode || cand.includes(mappedCode) || mappedCode.includes(cand)))
          );
        });
      });
    };

    const wardMatches = !hasWardTypes || isMatch(patientWards, this.preferredWardTypes);
    const deptMatches = !hasDepts || isMatch(patientDepts, this.preferredDepartments);
    const roomMatches = !hasRoomTypes || isMatch(patientRooms, this.preferredRoomTypes);

    // If both Ward and Department preferences are configured, patient must match department
    // and either match ward, match specific room, or have no ward label in record
    if (hasWardTypes && hasDepts) {
      if (!deptMatches) return false;
      if (hasRoomTypes && roomMatches) return true;
      const hasPatientWardInfo = patientWards.length > 0;
      return !hasPatientWardInfo || wardMatches;
    }

    if (hasDepts && hasRoomTypes) {
      return deptMatches && roomMatches;
    }

    if (hasWardTypes && hasRoomTypes) {
      return wardMatches || roomMatches;
    }

    if (hasDepts) {
      return deptMatches;
    }

    if (hasRoomTypes) {
      return roomMatches;
    }

    if (hasWardTypes) {
      return wardMatches;
    }

    return true;
  }

  private extractKnownWardTypes(): string[] {
    const set = new Set<string>();
    this.patients.forEach((p) => {
      if (p.ward) set.add(String(p.ward));
      if (p.wardName) set.add(String(p.wardName));
    });
    this.activePatients.forEach((p) => {
      if (p.wardTypeName) set.add(String(p.wardTypeName));
      if (p.wardType && isNaN(Number(p.wardType))) set.add(String(p.wardType));
    });
    return Array.from(set).filter(Boolean);
  }

  private extractKnownDepartments(): string[] {
    const set = new Set<string>();
    this.departments.forEach((d) => set.add(d.departmentName));
    this.patients.forEach((p) => {
      if (p.department) set.add(String(p.department));
      if (p.departmentName) set.add(String(p.departmentName));
    });
    this.activePatients.forEach((p) => {
      if (p.department) set.add(String(p.department));
      if (p.dischargedepartmnt) set.add(String(p.dischargedepartmnt));
      if (p.departmentName) set.add(String(p.departmentName));
      if (p.specialtyCodeValue) set.add(String(p.specialtyCodeValue));
    });
    return Array.from(set).filter(Boolean);
  }

  private extractKnownRoomTypes(): string[] {
    const set = new Set<string>();
    this.patients.forEach((p) => {
      if (p.room) set.add(String(p.room));
    });
    this.activePatients.forEach((p) => {
      if (p.roomTypeName) set.add(String(p.roomTypeName));
      if (p.roomType && isNaN(Number(p.roomType))) set.add(String(p.roomType));
    });
    return Array.from(set).filter(Boolean);
  }

  public openSettingsModal() {
    let mount = document.getElementById('settings-modal-mount');
    if (!mount) {
      mount = document.createElement('div');
      mount.id = 'settings-modal-mount';
      document.body.appendChild(mount);
    }

    new UserSettingsModal(
      mount,
      this.extractKnownWardTypes(),
      this.extractKnownDepartments(),
      this.extractKnownRoomTypes(),
      (newPrefs: UserPreferencesConfig) => {
        this.preferredWardTypes = newPrefs.preferredWardTypes;
        this.preferredDepartments = newPrefs.preferredDepartments;
        this.preferredRoomTypes = newPrefs.preferredRoomTypes;
        this.preferredHandoverNurse = newPrefs.preferredHandoverNurse;
        this.applyUserPreferencesFilter = true;
        this.render();
        if (this.currentTab === 'assign') {
          this.loadActivePatients(1);
        }
        appLogger.success('Preferable ward types, departments, and room types updated.');
      },
      () => {
        if (mount) {
          mount.innerHTML = '';
        }
      }
    );
  }

  private async preloadDepartments() {
    try {
      this.departments = await api.fetchDepartments();
    } catch (e) {
      console.warn('Preload departments failed', e);
    }
  }

  public async loadHandovers() {
    this.isLoading = true;
    this.render();
    appLogger.info('Syncing pending handovers and accepted count from hospital server...');

    try {
      const [pendingRes, countRes] = await Promise.allSettled([
        api.fetchHandoverIds(),
        api.fetchConfirmedHandoverCount(),
      ]);

      if (pendingRes.status === 'fulfilled') {
        this.patients = pendingRes.value;
        appLogger.success(`Fetched ${this.patients.length} pending handover record(s).`);
      } else {
        appLogger.error(`Failed to fetch pending handovers: ${pendingRes.reason?.message}`);
      }

      if (countRes.status === 'fulfilled') {
        this.totalAcceptedCount = countRes.value;
        appLogger.success(`Total accepted handovers: ${this.totalAcceptedCount} (pageCount).`);
      } else {
        appLogger.warn(`Failed to fetch accepted handover count: ${countRes.reason?.message}`);
      }

      if (!api.userName) {
        api.resolveUserName().then((name) => {
          if (name && name !== 'Nurse') {
            appLogger.info(`User profile verified: ${name}`);
            this.updateHeaderUserPill();
          }
        }).catch(() => { });
      }
    } catch (err: any) {
      appLogger.error(`Failed to sync handovers: ${err?.message}`);
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  public async loadActivePatients(page = 1) {
    this.isLoadingActivePatients = true;
    this.activePatientsPage = page;
    this.render();
    appLogger.info(`Loading inpatients directory via ward-service-patient-list (page ${page})...`);

    // Prepare server-side filters (only show arrived patients: arrivalStatus = 1)
    const serverFilters: Partial<WardServicePatientListFilter> = {
      page,
      arrivalStatus: 1,
      myPatient: this.activeMyPatientFilter ? 1 : 0,
      dischargedUnderSpecialty: this.activeSpecialtyFilter || null,
      wardType: this.activeWardFilter || null,
      roomType: this.activeRoomFilter || null,
    };

    // If no direct active filter is set, but user enabled "Showing Preferred",
    // map their stored preferences to server-side query filters (only when not searching)
    if (this.applyUserPreferencesFilter && this.hasActivePreferences() && !this.searchQuery.trim()) {
      if (!serverFilters.dischargedUnderSpecialty && this.preferredDepartments.length > 0) {
        for (const dept of this.preferredDepartments) {
          const code = SPECIALTY_CODE_MAP[dept.toUpperCase()] || (dept.length <= 4 ? dept.toUpperCase() : null);
          if (code) {
            serverFilters.dischargedUnderSpecialty = code;
            break;
          }
        }
      }

      if (!serverFilters.wardType && this.preferredWardTypes.length > 0) {
        for (const ward of this.preferredWardTypes) {
          const code = WARD_CODE_MAP[ward.toUpperCase()] || (!isNaN(Number(ward)) ? ward : null);
          if (code) {
            serverFilters.wardType = code;
            break;
          }
        }
      }

      if (!serverFilters.roomType && this.preferredRoomTypes.length > 0) {
        for (const room of this.preferredRoomTypes) {
          const code = ROOM_CODE_MAP[room.toUpperCase()] || (!isNaN(Number(room)) ? room : null);
          if (code) {
            serverFilters.roomType = code;
            break;
          }
        }
      }
    }

    const isFiltered = Boolean(
      serverFilters.dischargedUnderSpecialty ||
      serverFilters.wardType ||
      serverFilters.roomType ||
      serverFilters.myPatient ||
      (this.applyUserPreferencesFilter && this.hasActivePreferences())
    );

    try {
      if (this.searchQuery.trim()) {
        const { result, totalCount } = await api.searchActiveInpatients(this.searchQuery, serverFilters);
        this.activePatients = result;
        this.activePatientsTotalPages = 1;
        this.activePatientsTotalCount = totalCount;
        appLogger.info(`Found ${result.length} patient(s) matching "${this.searchQuery}".`);
      } else if (isFiltered) {
        // Fetch ALL filtered inpatients across pages (don't if not filtered)
        const { result, totalCount } = await api.fetchAllFilteredInpatients(serverFilters);
        this.activePatients = result;
        this.activePatientsTotalPages = 1;
        this.activePatientsTotalCount = totalCount || result.length;
        appLogger.success(`Loaded all ${result.length} filtered inpatient(s).`);
      } else {
        const { result, pageCount, totalCount } = await api.fetchActiveInpatients(page, serverFilters);
        this.activePatients = result;
        this.activePatientsTotalPages = pageCount || 1;
        this.activePatientsTotalCount = totalCount || result.length;
        appLogger.success(`Loaded ${result.length} inpatient(s) (page ${page} of ${this.activePatientsTotalPages}, ${this.activePatientsTotalCount} total).`);
      }
    } catch (err: any) {
      appLogger.error(`Failed to load active inpatients: ${err?.message}`);
    } finally {
      this.isLoadingActivePatients = false;
      this.render();
      // After fetching, pull last notes, check if handover notes or not and whom given to, and update page
      this.verifyPatientsLatestNotes(this.activePatients);
    }
  }

  private noteVerificationSeq = 0;

  /**
   * Concurrently check the latest note for each patient in the list,
   * verify whether it is a handover note, identify whom it was given to,
   * and dynamically update each patient card in the DOM.
   */
  private async verifyPatientsLatestNotes(patients: ActiveInpatient[]) {
    if (!patients || patients.length === 0) return;
    const currentSeq = ++this.noteVerificationSeq;
    const targetPatients = [...patients];

    const concurrency = 5;
    let index = 0;

    const worker = async () => {
      while (index < targetPatients.length) {
        if (currentSeq !== this.noteVerificationSeq) return; // Stale run aborted
        const p = targetPatients[index++];
        if (!p) break;
        const hosp = String(p.hospitalNumber);
        const ip = String(p.ipNumber);

        try {
          const { isHandover, record } = await handoverTrackingService.checkPatientLastNote(hosp, ip);
          if (currentSeq !== this.noteVerificationSeq) return;
          this.updatePatientCardNoteStatus(hosp, ip, isHandover ? record : null);
        } catch (e) {
          console.warn(`Failed note verification for ${hosp}/${ip}:`, e);
        }
      }
    };

    const workers = [];
    for (let i = 0; i < Math.min(concurrency, targetPatients.length); i++) {
      workers.push(worker());
    }
    await Promise.all(workers);
  }

  /**
   * Dynamically update a patient card in the DOM to reflect handover note status
   */
  private updatePatientCardNoteStatus(hosp: string, ip: string, record: HandoverAssignmentRecord | null) {
    const card = this.container.querySelector(`.assign-patient-card[data-key="${hosp}-${ip}"]`) as HTMLElement;
    if (!card) return;

    const actionContainer = card.querySelector('.patient-card-action');
    let metaAssigned = card.querySelector('.meta-assigned') as HTMLElement;

    if (record) {
      card.classList.add('has-assigned');
      const isAccepted =
        String(record.ackStatus).toUpperCase() === 'ACKNOWLEDGED' ||
        String(record.ackStatus).toUpperCase() === 'NTC0002';
      const nurseFirst = (record.targetNurseName || 'Nurse').split(' ')[0];

      if (!metaAssigned && actionContainer) {
        metaAssigned = document.createElement('div');
        metaAssigned.className = 'meta-tag meta-assigned meta-assigned-gray';
        actionContainer.prepend(metaAssigned);
      }

      if (metaAssigned) {
        metaAssigned.className = 'meta-tag meta-assigned meta-assigned-gray';
        metaAssigned.setAttribute(
          'title',
          isAccepted
            ? `Accepted by ${record.targetNurseName}`
            : `Assigned to ${record.targetNurseName} (Pending Acknowledgment)`
        );
        metaAssigned.innerHTML = `<span>${isAccepted ? 'Accepted' : 'Assigned'}: ${nurseFirst}</span>`;
      }

      const btn = card.querySelector('.btn-patient-assign') as HTMLButtonElement;
      if (btn) {
        btn.classList.add('is-assigned-state');
        const hasNurse = !!this.assignTargetNurse;
        btn.title = hasNurse
          ? `Reassign to ${this.assignTargetNurse!.userName}`
          : `Assigned to ${record.targetNurseName} • Tap to reassign`;
        const textSpan = btn.querySelector('.btn-assign-text');
        if (textSpan) textSpan.textContent = 'Reassign';
      }
    } else {
      card.classList.remove('has-assigned');
      if (metaAssigned) {
        metaAssigned.remove();
      }
      const btn = card.querySelector('.btn-patient-assign') as HTMLButtonElement;
      if (btn) {
        btn.classList.remove('is-assigned-state');
        const hasNurse = !!this.assignTargetNurse;
        btn.title = hasNurse ? `Assign to ${this.assignTargetNurse!.userName}` : 'Select nurse to assign';
        const textSpan = btn.querySelector('.btn-assign-text');
        if (textSpan) textSpan.textContent = 'Assign';
      }
    }
  }

  private updateHeaderUserPill() {
    const displayName = api.getUserDisplayName();
    const loginId = api.loginId || '';
    const empId = api.empId ? `EMP: ${api.empId}` : (loginId ? `ID: ${loginId}` : 'Desk');
    const userTooltip = loginId ? `${displayName} (${empId}) • Tap for Settings` : displayName;

    const userLabel = this.container.querySelector('.user-label');
    const userAvatar = this.container.querySelector('.user-avatar');
    const userPill = this.container.querySelector('#btn-open-settings');

    if (userLabel) userLabel.textContent = displayName;
    if (userAvatar) userAvatar.textContent = displayName.charAt(0).toUpperCase();
    if (userPill) userPill.setAttribute('title', userTooltip);
  }

  private getFilteredQueuePatients(): PatientHandoverItem[] {
    let filtered = this.patients;

    // Only show patients if they have arrived
    filtered = filtered.filter((p) => getPatientArrivalStatus(p).isArrived);

    if (!this.searchQuery.trim()) {
      return filtered;
    }

    const q = this.searchQuery.toLowerCase().trim();
    const cleanDocQ = q.replace(/^dr\.?\s+/i, '');
    return filtered.filter((p) => {
      const name = (p.patientName || '').toLowerCase();
      const hosp = String(p.hospitalNumber || '').toLowerCase();
      const doc = (p.doctorName || '').toLowerCase();
      return name.includes(q) || hosp.includes(q) || doc.includes(q) || (cleanDocQ ? doc.includes(cleanDocQ) : false);
    });
  }

  private getFilteredActivePatients(): ActiveInpatient[] {
    let filtered = this.activePatients;

    // Only show patients if they have arrived
    filtered = filtered.filter((p) => getPatientArrivalStatus(p).isArrived);

    if (!this.searchQuery.trim()) {
      // Filter by user's stored preferences (if active) only when not actively searching
      if (this.applyUserPreferencesFilter && this.hasActivePreferences()) {
        filtered = filtered.filter((p) => this.doesPatientMatchPreferences(p));
      }
      return filtered;
    }

    const q = this.searchQuery.toLowerCase().trim();
    const cleanDocQ = q.replace(/^dr\.?\s+/i, '');

    return filtered.filter((p) => {
      const name = (p.displayName || p.firstName || p.patientName || '').toLowerCase();
      const hosp = String(p.hospitalNumber || '').toLowerCase();
      const doc = (p.doctorName || p.DoctorName || '').toLowerCase();

      // Strict exact match on patient name, hospital number, or doctor name
      const matchesName = name.includes(q);
      const matchesHosp = hosp.includes(q);
      const matchesDoc = doc.includes(q) || (cleanDocQ ? doc.includes(cleanDocQ) : false);

      return matchesName || matchesHosp || matchesDoc;
    });
  }

  // ── Assign Tab: Nurse Picker Methods ──────────────────────────────────

  private async loadInitialNurses() {
    this.isSearchingAssignNurse = true;
    this.updateNursePickerDOM();
    try {
      this.assignNurseResults = await api.searchNurses('', 1, 30);
    } catch (e: any) {
      console.warn('Could not load initial nurse list', e);
    } finally {
      this.isSearchingAssignNurse = false;
      this.updateNursePickerDOM();
    }
  }

  private async searchAssignNurses(query: string) {
    this.assignNurseSearchQuery = query;
    this.isSearchingAssignNurse = true;
    this.updateNursePickerDOM();

    try {
      this.assignNurseResults = await api.searchNurses(query, 1, 40);
    } catch (e: any) {
      console.error('Failed searching nurses', e);
    } finally {
      this.isSearchingAssignNurse = false;
      this.updateNursePickerDOM();
    }
  }

  private updateNursePickerDOM() {
    const listEl = this.container.querySelector('#assign-nurse-results') as HTMLElement;
    if (!listEl) return;

    if (this.isSearchingAssignNurse) {
      listEl.innerHTML = this.renderNurseSkeletons(4);
      return;
    }

    if (this.assignNurseResults.length === 0) {
      listEl.innerHTML = `
        <div class="nurse-picker-empty">
          <div class="nurse-empty-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="17" y1="11" x2="23" y2="11"/>
            </svg>
          </div>
          <p class="nurse-empty-title">No nurses found</p>
          <p class="nurse-empty-sub">${this.assignNurseSearchQuery ? `No matches for "${this.assignNurseSearchQuery}"` : 'Type a nurse name or employee ID to search'}</p>
        </div>
      `;
      return;
    }

    listEl.innerHTML = this.assignNurseResults.map((nurse) => {
      const isSelected = this.assignTargetNurse?.loginId === nurse.loginId;
      const initials = (nurse.userName || nurse.loginId || 'N').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'N';
      const empId = nurse.employeeId || nurse.empId || nurse.id;
      return `
        <button class="nurse-pick-item ${isSelected ? 'selected' : ''}" data-nurse-login="${nurse.loginId}" data-nurse-name="${nurse.userName}" data-nurse-id="${nurse.id}" data-emp-id="${empId}" type="button">
          <span class="nurse-pick-avatar">${initials}</span>
          <span class="nurse-pick-info">
            <span class="nurse-pick-name">${nurse.userName}</span>
            <span class="nurse-pick-id">${empId ? `<span class="nurse-pick-id-tag">EMP: ${empId}</span>` : (nurse.loginId ? `<span class="nurse-pick-id-tag">ID: ${nurse.loginId}</span>` : '')}</span>
          </span>
          <span class="nurse-pick-check-wrap">
            <span class="nurse-pick-check-circle ${isSelected ? 'checked' : ''}">
              ${isSelected ? `
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              ` : ''}
            </span>
          </span>
        </button>
      `;
    }).join('');

    // Attach click events
    listEl.querySelectorAll('.nurse-pick-item').forEach((btn) => {
      btn.addEventListener('click', () => {
        const loginId = btn.getAttribute('data-nurse-login') || '';
        const userName = btn.getAttribute('data-nurse-name') || '';
        const id = btn.getAttribute('data-nurse-id') || '';
        const empId = btn.getAttribute('data-emp-id') || id;
        this.assignTargetNurse = { id, loginId, userName, empId, employeeId: empId };
        this.preferredHandoverNurse = this.assignTargetNurse;
        this.showAssignNursePicker = false;
        if (api.loginId) {
          userService.saveUserPreferences(api.loginId, {
            preferredHandoverNurse: this.assignTargetNurse,
          }).catch(console.warn);
        }
        this.render();
        appLogger.info(`Assign target set to: ${userName} (${loginId})`);
      });
    });
  }

  // ── Render ────────────────────────────────────────────────────────────

  public render() {
    const isSearchFocused = document.activeElement?.id === 'search-input';
    const searchCursor = isSearchFocused ? (document.activeElement as HTMLInputElement).selectionStart : null;

    const queueFiltered = this.getFilteredQueuePatients();
    const activeFiltered = this.getFilteredActivePatients();
    const displayName = api.getUserDisplayName();
    const loginId = api.loginId || '';
    const empId = api.empId ? `EMP: ${api.empId}` : (loginId ? `ID: ${loginId}` : 'Desk');
    const userTooltip = loginId ? `${displayName} (${empId}) • Tap for Settings` : displayName;
    const pendingCount = queueFiltered.length;

    this.container.innerHTML = `
      <div class="app-shell">
        <!-- Compact Top Nav -->
        <header class="app-navbar">
          <div class="navbar-content">
            <div class="brand-wrapper">
              <img src="/vyko.webp" alt="Vyko" class="brand-logo-img" width="28" height="28" />
              <div class="brand-headings">
                <div class="brand-name">VIMS&RC</div>
                <div class="brand-subline">Nurse Handover</div>
              </div>
            </div>

            <div class="navbar-actions">
              <button class="user-pill-btn" id="btn-user-profile" title="${userTooltip}">
                <div class="user-avatar">${displayName.charAt(0).toUpperCase()}</div>
                <span class="user-label">${displayName.split(' ')[0]}</span>
              </button>

              <button class="btn-icon btn-theme-toggle" id="btn-theme-toggle" title="${isDarkMode() ? 'Switch to Light Mode' : 'Switch to Dark Mode'}">
                ${isDarkMode() ? `
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                  </svg>
                ` : `
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="5"/>
                    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                  </svg>
                `}
              </button>

              <button class="btn-icon btn-signout" id="btn-logout" title="Sign out">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </div>
          </div>
        </header>

        <!-- Main Dashboard Container -->
        <main class="app-container">
          <!-- Unified Single Stat Card with Separators -->
          <section class="stat-card-unified" role="region" aria-label="Shift Overview Statistics">
            <!-- 1. Pending Column (Clickable to switch to Acknowledge tab) -->
            <button
              type="button"
              class="stat-unit ${this.currentTab === 'acknowledge' ? 'stat-unit-active' : ''}"
              id="stat-unit-pending"
              title="View Pending Handovers"
            >
              <div class="stat-unit-header">
                <span class="stat-unit-label">Pending</span>
              </div>
              <div class="stat-unit-value stat-val-orange">${pendingCount}</div>
              <div class="stat-unit-hint">To Acknowledge</div>
            </button>

            <!-- Separator 1 -->
            <div class="stat-unit-divider"></div>

            <!-- 2. Inpatients Column (Clickable to switch to Assign tab) -->
            <button
              type="button"
              class="stat-unit ${this.currentTab === 'assign' ? 'stat-unit-active' : ''}"
              id="stat-unit-inpatients"
              title="View Active Inpatients"
            >
              <div class="stat-unit-header">
                <span class="stat-unit-label">Inpatients</span>
              </div>
              <div class="stat-unit-value stat-val-blue">${this.activePatientsTotalCount > 0 ? this.activePatientsTotalCount : this.activePatients.length}</div>
              <div class="stat-unit-hint">Active Ward</div>
            </button>

            <!-- Separator 2 -->
            <div class="stat-unit-divider"></div>

            <!-- 3. Accepted Column -->
            <div class="stat-unit" id="stat-unit-accepted">
              <div class="stat-unit-header">
                <span class="stat-unit-label">Accepted</span>
              </div>
              <div class="stat-unit-value stat-val-green">${this.totalAcceptedCount}</div>
              <div class="stat-unit-hint">Completed</div>
            </div>
          </section>

          <!-- Segmented Tabs: Acknowledge / Assign -->
          <div class="tab-controls-bar">
            <div class="ios-segmented-control" role="tablist">
              <button class="segmented-option ${this.currentTab === 'acknowledge' ? 'active' : ''}" id="tab-acknowledge" role="tab" type="button">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>Acknowledge</span>
                <span class="segmented-badge">${pendingCount}</span>
              </button>

              <button class="segmented-option ${this.currentTab === 'assign' ? 'active' : ''}" id="tab-assign" role="tab" type="button">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <polyline points="16 11 18 13 22 9"/>
                </svg>
                <span>Assign</span>
                <span class="segmented-badge">${activeFiltered.length}</span>
              </button>
            </div>
          </div>

          <!-- Search + Refresh Row -->
          <div class="search-action-row">
            <div class="ios-search-wrapper">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                id="search-input"
                placeholder="Search by name, H.No, doctor..."
                value="${this.searchQuery}"
                autocomplete="off"
              />
              ${this.searchQuery ? `
                <button class="ios-search-clear" id="btn-clear-search" title="Clear search">✕</button>
              ` : ''}
            </div>

            ${this.renderSearchFilterButton()}

            <button class="btn-icon btn-refresh-bar" id="btn-refresh" ${this.isLoading || this.isLoadingActivePatients ? 'disabled' : ''} title="Sync data">
              <svg class="${this.isLoading || this.isLoadingActivePatients ? 'rotating' : ''}" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>

          <!-- ═══ TAB 1: ACKNOWLEDGE ═══ -->
          ${this.currentTab === 'acknowledge' ? `
            ${pendingCount > 0 ? `
              <div class="tab-action-bar">
                <button
                  class="btn btn-accept-all btn-pill"
                  id="btn-accept-all"
                  ${this.isLoading || pendingCount === 0 ? 'disabled' : ''}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span>Acknowledge All (${pendingCount})</span>
                </button>
              </div>
            ` : ''}

            <div class="patient-list-group" id="patient-list-container">
              ${this.isLoading ? this.renderPatientSkeletons('acknowledge', 4) : queueFiltered.length === 0 ? `
                <div class="empty-apple-state">
                  <div class="empty-apple-state-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <h3>${this.searchQuery ? 'No Results' : 'All Caught Up'}</h3>
                  <p>
                    ${this.searchQuery
            ? 'No handovers match your search.'
            : (this.hasActivePreferences() && this.applyUserPreferencesFilter
              ? 'No pending handovers match your preferences.'
              : 'Every pending handover has been acknowledged.')}
                  </p>
                  ${(this.hasActivePreferences() && this.applyUserPreferencesFilter) ? `
                    <button class="btn btn-secondary btn-pill" id="btn-empty-show-all" style="margin-top: 0.5rem;">
                      Show All
                    </button>
                  ` : ''}
                </div>
              ` : `
                ${queueFiltered.map((p, idx) => this.renderAcknowledgeCard(p, idx)).join('')}
              `}
            </div>

            ${pendingCount > 0 ? `
              <div class="acknowledge-floating-bar animate-slide-up" id="acknowledge-bottom-bar" style="${this.isLoading || queueFiltered.length === 0 ? 'display: none;' : ''}">
                <div class="acknowledge-glass-content">
                  <div class="acknowledge-glass-info">
                    <span class="acknowledge-count-badge" id="bottom-ack-count">${queueFiltered.length}</span>
                    <span class="acknowledge-glass-text">Pending handover${queueFiltered.length === 1 ? '' : 's'}</span>
                  </div>
                  <button
                    class="btn btn-acknowledge-pill"
                    id="btn-acknowledge-all-bottom"
                    type="button"
                    ${this.isLoading ? 'disabled' : ''}
                    title="Acknowledge all pending handovers"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>Acknowledge All</span>
                  </button>
                </div>
              </div>
            ` : ''}
          ` : ''}

          <!-- ═══ TAB 2: ASSIGN ═══ -->
          ${this.currentTab === 'assign' ? `
            <!-- Shift Handover Recipient Panel -->
            <div class="assign-duty-panel">
              ${this.assignTargetNurse ? `
                <div class="duty-card-selected">
                  <div class="duty-card-left">
                    <div class="duty-nurse-avatar">
                      ${this.assignTargetNurse.userName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                      <span class="duty-online-badge" title="Active shift recipient"></span>
                    </div>
                    <div class="duty-nurse-details">
                      <div class="duty-tag-label">INCOMING SHIFT NURSE</div>
                      <div class="duty-nurse-name">${this.assignTargetNurse.userName}</div>
                      <div class="duty-nurse-sub">${this.assignTargetNurse.loginId ? `ID: ${this.assignTargetNurse.loginId}` : ''}</div>
                    </div>
                  </div>
                  <button class="btn-duty-action" id="btn-change-assign-nurse" title="Change recipient nurse">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    <span>Change</span>
                  </button>
                </div>
              ` : `
                <div class="duty-card-empty">
                  <div class="duty-card-left">
                    <div class="duty-empty-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <line x1="19" y1="8" x2="19" y2="14"/>
                        <line x1="22" y1="11" x2="16" y2="11"/>
                      </svg>
                    </div>
                    <div class="duty-empty-details">
                      <div class="duty-empty-title">Select Shift Nurse</div>
                      <div class="duty-empty-desc">Choose the incoming nurse taking over your patients</div>
                    </div>
                  </div>
                  <button class="btn btn-primary btn-pill duty-select-btn" id="btn-open-assign-nurse">
                    <span>Select Nurse</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                </div>
              `}
            </div>

            <!-- Toolbar & Pagination -->
            <div class="assign-toolbar">
              <div class="assign-toolbar-left">
                <span class="assign-count-badge">
                  ${this.searchQuery.trim()
          ? `<strong>${activeFiltered.length}</strong> matching "${this.searchQuery.trim()}"`
          : `<strong>${activeFiltered.length}</strong> of ${this.activePatientsTotalCount || activeFiltered.length} inpatients`}
                </span>
                ${activeFiltered.length > 0 ? `
                  <button class="btn-text-action" id="btn-toggle-select-all" title="Select all on this page">
                    ${this.selectedAssignPatientKeys.size === activeFiltered.length && activeFiltered.length > 0 ? 'Deselect All' : 'Select All'}
                  </button>
                ` : ''}
              </div>

              <div class="assign-toolbar-right">
                ${!this.searchQuery.trim() ? `
                  <div class="assign-pagination-group">
                    <button
                      class="btn-page-step"
                      id="btn-active-prev-page"
                      ${this.activePatientsPage <= 1 || this.isLoadingActivePatients ? 'disabled' : ''}
                      title="Previous page"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="15 18 9 12 15 6"/>
                      </svg>
                    </button>
                    <span class="page-indicator-text">${this.activePatientsPage} / ${this.activePatientsTotalPages}</span>
                    <button
                      class="btn-page-step"
                      id="btn-active-next-page"
                      ${this.activePatientsPage >= this.activePatientsTotalPages || this.isLoadingActivePatients ? 'disabled' : ''}
                      title="Next page"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>
                  </div>
                ` : ''}
              </div>
            </div>

            ${!this.hasActivePreferences() && !this.searchQuery.trim() ? `
              <div class="filter-prompt-banner" id="banner-set-filter">
                <div class="filter-prompt-banner-content">
                  <span class="banner-icon">💡</span>
                  <span><strong>No filter configured:</strong> Showing all hospital inpatients. Set your Ward or Department to view only your patients and verify handovers.</span>
                </div>
                <button type="button" class="btn-banner-action" id="btn-banner-set-filter">Set Filter</button>
              </div>
            ` : ''}

            <div class="patient-list-group" id="active-patient-list-container">
              ${this.isLoadingActivePatients ? this.renderPatientSkeletons('assign', 5) : activeFiltered.length === 0 ? `
                <div class="empty-apple-state">
                  <div class="empty-apple-state-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </div>
                  <h3>No Matching Inpatients</h3>
                  <p>No inpatients match your current filters.</p>
                </div>
              ` : `
                ${activeFiltered.map((p, idx) => this.renderAssignCard(p, idx)).join('')}
              `}
            </div>
          ` : ''}
        </main>

        <!-- Nurse Picker Modal (if open) -->
        ${this.showAssignNursePicker ? `
          <div class="batch-modal-overlay vyko-modal-overlay" id="nurse-picker-overlay">
            <div class="batch-modal-card nurse-picker-modal-card vyko-modal-card animate-scale-up" role="dialog" aria-modal="true">
              <div class="sheet-drag-handle"></div>
              <div class="batch-modal-header vyko-modal-header">
                <div class="modal-title-group">
                  <div>
                    <h2 class="batch-modal-title vyko-modal-title">Select Shift Nurse</h2>
                    <p class="batch-modal-subtitle vyko-modal-sub">Choose the nurse taking over patient care</p>
                  </div>
                </div>
                <button class="btn-icon btn-modal-close vyko-modal-close" id="btn-close-nurse-picker" title="Close">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <div class="batch-modal-body vyko-modal-body" style="padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 0.85rem;">
                <div class="nurse-picker-search-box">
                  <svg class="nurse-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    id="assign-nurse-search"
                    placeholder="Search nurse by name or login ID..."
                    value="${this.assignNurseSearchQuery}"
                    autocomplete="off"
                    autofocus
                  />
                  ${this.assignNurseSearchQuery ? `<button class="nurse-search-clear" id="btn-clear-nurse-search">✕</button>` : ''}
                </div>

                <div class="nurse-picker-modal-list" id="assign-nurse-results">
                  ${this.renderNurseSkeletons(4)}
                </div>
              </div>
            </div>
          </div>
        ` : ''}

        <div id="dashboard-modal-container"></div>
      </div>
    `;

    this.attachEvents();
    if (isSearchFocused) {
      const searchInp = this.container.querySelector('#search-input') as HTMLInputElement | null;
      if (searchInp) {
        searchInp.focus();
        if (searchCursor !== null) {
          try { searchInp.setSelectionRange(searchCursor, searchCursor); } catch { }
        }
      }
    }
  }

  // ── Preferences Filter Button (Beside Search, Assign Tab Only) ──────────

  private renderSearchFilterButton(): string {
    // Only in Assign tab
    if (this.currentTab !== 'assign') {
      return '';
    }

    if (!this.hasActivePreferences()) {
      return `
        <button
          type="button"
          class="btn-search-filter is-off is-no-prefs"
          id="btn-set-filter-prompt"
          title="No filter set — click to choose your department or ward"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          <span class="btn-search-filter-text">Set Filter</span>
        </button>
      `;
    }

    const allTags = [
      ...this.preferredWardTypes,
      ...this.preferredDepartments,
      ...this.preferredRoomTypes,
    ].filter(Boolean);

    const isFiltered = this.applyUserPreferencesFilter;

    return `
      <button
        type="button"
        class="btn-search-filter ${isFiltered ? 'is-active' : 'is-off'}"
        id="btn-toggle-pref-filter"
        title="${isFiltered ? `Filtered by ${allTags.join(', ')} — click to show all` : 'Preferences filter paused — click to activate'}"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        <span class="btn-search-filter-text">Filtered</span>
        ${allTags.length > 0 ? `<span class="btn-search-filter-badge">${allTags.length}</span>` : ''}
      </button>
    `;
  }

  // ── Skeleton Loaders ──────────────────────────────────────────────────

  private renderPatientSkeletons(tab: 'acknowledge' | 'assign' = 'assign', count = 5): string {
    const isAssign = tab === 'assign';
    return Array.from({ length: count })
      .map(
        (_, i) => `
        <div class="patient-card ${isAssign ? 'assign-patient-card' : ''} skeleton-card">
          <div class="skeleton-gender" style="width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;"></div>
          <div class="patient-card-content">
            <div class="skeleton-line" style="width: ${40 + ((i * 11) % 25)}%; height: 16px; border-radius: 4px;"></div>
            ${isAssign ? `
              <div class="patient-card-row" style="display:flex; gap:0.4rem; margin-top:0.25rem;">
                <div class="skeleton-line" style="width: 65px; height: 18px; border-radius: 4px;"></div>
                <div class="skeleton-line" style="width: 45px; height: 18px; border-radius: 4px;"></div>
              </div>
              <div class="patient-card-row" style="display:flex; gap:0.4rem; margin-top:0.25rem;">
                <div class="skeleton-line" style="width: 110px; height: 18px; border-radius: 4px;"></div>
                <div class="skeleton-line" style="width: 50px; height: 18px; border-radius: 4px;"></div>
              </div>
              <div class="patient-card-row" style="display:flex; gap:0.4rem; margin-top:0.25rem;">
                <div class="skeleton-line" style="width: 90px; height: 14px; border-radius: 4px; opacity: 0.7;"></div>
              </div>
            ` : `
              <div class="patient-card-meta">
                <div class="skeleton-line" style="width: 72px; height: 18px; border-radius: 4px;"></div>
                <div class="skeleton-line" style="width: 68px; height: 18px; border-radius: 4px;"></div>
                <div class="skeleton-line" style="width: 100px; height: 18px; border-radius: 4px; opacity: 0.7;"></div>
              </div>
            `}
          </div>
          <div class="patient-card-action">
            <div class="skeleton-line" style="width: ${isAssign ? '84px' : '76px'}; height: 34px; border-radius: 980px; flex-shrink: 0;"></div>
          </div>
        </div>
      `
      )
      .join('');
  }

  private renderNurseSkeletons(count = 4): string {
    return Array.from({ length: count })
      .map(
        (_, i) => `
        <div class="skeleton-nurse-item">
          <div class="skeleton-circle" style="width: 32px; height: 32px; flex-shrink: 0;"></div>
          <div style="flex: 1; display: flex; flex-direction: column; gap: 0.35rem; min-width: 0;">
            <div class="skeleton-line" style="width: ${45 + ((i * 19) % 30)}%; height: 13px; border-radius: 4px;"></div>
            <div class="skeleton-line" style="width: 35%; height: 10px; border-radius: 4px; opacity: 0.6;"></div>
          </div>
        </div>
      `
      )
      .join('');
  }

  // ── Card Renderers ────────────────────────────────────────────────────

  private renderAcknowledgeCard(patient: PatientHandoverItem, index: number): string {
    const patientName = getPatientDisplayName(patient, `Patient #${index + 1}`);
    const hosp = String(patient.hospitalNumber || 'N/A');
    const ip = String(patient.ipNumber || 'N/A');
    const dept = patient.departmentName || patient.department || null;
    const doctor = formatDoctorName(patient.doctorName);
    const gender = resolvePatientGender(patient, this.activePatients);
    const genderTitle = gender === 'male' ? 'Male Patient' : gender === 'female' ? 'Female Patient' : 'Patient';

    return `
      <div class="patient-card patient-card-clean" data-hosp="${hosp}" data-ip="${ip}">
        <div class="patient-gender-avatar gender-${gender}" title="${genderTitle}" aria-hidden="true">
          ${renderPatientGenderIcon(gender)}
        </div>

        <div class="patient-card-content">
          <div class="patient-card-name" title="${patientName}">${patientName}</div>
          <div class="patient-card-meta">
            <div class="meta-tag meta-id">
              <span class="meta-k">H.No</span>
              <span class="meta-v">${hosp}</span>
            </div>
            <div class="meta-tag meta-id">
              <span class="meta-k">IP</span>
              <span class="meta-v">${ip}</span>
            </div>
            ${dept ? `
              <div class="meta-tag meta-dept" title="${dept}">
                <span>${dept}</span>
              </div>
            ` : ''}
            ${doctor ? `
              <div class="meta-tag meta-doc" title="Attending: ${doctor}">
                <span>${doctor}</span>
              </div>
            ` : ''}
          </div>
        </div>

        <div class="patient-card-action">
          <button type="button" class="btn-patient-action btn-patient-accept btn-accept-single" data-index="${index}" title="Acknowledge handover">
            <span>Accept</span>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  private renderAssignCard(patient: ActiveInpatient, index: number): string {
    const patientName = getPatientDisplayName(patient, `Inpatient #${index + 1}`);
    const hosp = String(patient.hospitalNumber || 'N/A');
    const ip = String(patient.ipNumber || 'N/A');
    const dept = patient.departmentName || patient.specialtyCodeValue || '';
    const doctor = formatDoctorName(patient.doctorName || patient.DoctorName);
    const patientKey = `${hosp}-${ip}`;
    const isSelected = this.selectedAssignPatientKeys.has(patientKey);
    const hasNurse = !!this.assignTargetNurse;
    const assignedRecord = handoverTrackingService.getAssignment(hosp, ip);
    const isAssigned = !!assignedRecord;
    const isAccepted =
      String(assignedRecord?.ackStatus).toUpperCase() === 'ACKNOWLEDGED' ||
      String(assignedRecord?.ackStatus).toUpperCase() === 'NTC0002';
    const assignedNurseFirst = assignedRecord ? assignedRecord.targetNurseName.split(' ')[0] : '';
    const gender = resolvePatientGender(patient, this.activePatients);
    const genderTitle = gender === 'male' ? 'Male Patient' : gender === 'female' ? 'Female Patient' : 'Patient';

    const rawAge =
      patient.age !== undefined && patient.age !== null
        ? patient.age
        : (patient as any).ageYy ?? (patient as any).patientAge;
    const cleanAge = rawAge !== undefined && rawAge !== null ? String(rawAge).trim() : '';
    const hasAge = cleanAge !== '' && cleanAge.toLowerCase() !== 'null' && cleanAge.toLowerCase() !== 'undefined';
    const ageText = hasAge ? (/^\d+$/.test(cleanAge) ? (cleanAge === '0' ? '<1 Y' : `${cleanAge} Y`) : cleanAge) : '';

    const rawUnit =
      patient.deparmentUnit !== undefined && patient.deparmentUnit !== null
        ? patient.deparmentUnit
        : (patient as any).departmentUnit ?? (patient as any).unitNo ?? (patient as any).unit;
    const cleanUnit = rawUnit !== undefined && rawUnit !== null ? String(rawUnit).trim() : '';
    const hasUnit = cleanUnit !== '' && cleanUnit.toLowerCase() !== 'null' && cleanUnit.toLowerCase() !== 'undefined';
    const unitText = hasUnit ? cleanUnit.replace(/^unit\s*[-:#]?\s*/i, '') || cleanUnit : '';

    return `
      <div class="patient-card assign-patient-card ${isSelected ? 'is-selected' : ''} ${isAssigned ? 'has-assigned' : ''}" data-key="${patientKey}" data-hosp="${hosp}" data-ip="${ip}">
        <div class="patient-gender-avatar gender-${gender}" title="${genderTitle}" aria-hidden="true">
          ${renderPatientGenderIcon(gender)}
        </div>

        <div class="patient-card-content">
          <div class="patient-card-name" title="${patientName}">${patientName}</div>
          
          <div class="patient-card-row patient-card-row-ids">
            <div class="meta-tag meta-id">
              <span class="meta-k">H.No</span>
              <span class="meta-v">${hosp}</span>
            </div>
            ${ageText ? `
              <div class="meta-tag meta-id meta-age" title="Age: ${ageText}">
                <span class="meta-k">Age</span>
                <span class="meta-v">${ageText}</span>
              </div>
            ` : ''}
          </div>

          <div class="patient-card-row patient-card-row-dept">
            ${dept ? `
              <div class="meta-tag meta-dept" title="${dept}">
                <span>${dept}</span>
              </div>
            ` : ''}
            ${unitText ? `
              <div class="meta-tag meta-id meta-unit" title="Unit: ${unitText}">
                <span class="meta-k">Unit</span>
                <span class="meta-v">${unitText}</span>
              </div>
            ` : ''}
          </div>

          ${doctor ? `
            <div class="patient-card-row patient-card-row-doc">
              <div class="meta-tag meta-doc" title="Attending: ${doctor}">
                <span>${doctor}</span>
              </div>
            </div>
          ` : ''}
        </div>

        <div class="patient-card-action">
          ${assignedRecord ? `
            <div class="meta-tag meta-assigned meta-assigned-gray" title="${isAccepted ? `Accepted by ${assignedRecord.targetNurseName}` : `Assigned to ${assignedRecord.targetNurseName} (Pending Acknowledgment)`}">
              <span>${isAccepted ? 'Accepted' : 'Assigned'}: ${assignedNurseFirst}</span>
            </div>
          ` : ''}
          <button
            type="button"
            class="btn-patient-action btn-patient-assign btn-give-handover-active ${hasNurse ? 'has-nurse' : ''} ${isAssigned ? 'is-assigned-state' : ''}"
            data-index="${index}"
            title="${hasNurse ? (isAssigned ? `Reassign to ${this.assignTargetNurse!.userName}` : `Assign to ${this.assignTargetNurse!.userName}`) : (isAssigned ? `Assigned to ${assignedRecord!.targetNurseName} • Tap to reassign` : 'Select nurse to assign')}"
          >
            <span class="btn-assign-text">${isAssigned ? 'Reassign' : 'Assign'}</span>
            <svg class="btn-assign-arrow" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  // ── Events ────────────────────────────────────────────────────────────

  private attachEvents() {
    // Settings
    this.container.querySelector('#btn-open-settings')?.addEventListener('click', () => this.openSettingsModal());
    this.container.querySelector('#btn-user-profile')?.addEventListener('click', () => this.openSettingsModal());

    // Toggle preferences filter
    this.container.querySelector('#btn-toggle-pref-filter')?.addEventListener('click', () => {
      this.applyUserPreferencesFilter = !this.applyUserPreferencesFilter;
      if (this.currentTab === 'assign') {
        this.loadActivePatients(1);
      } else {
        this.render();
      }
    });

    // Prompt to set filter
    this.container.querySelector('#btn-set-filter-prompt')?.addEventListener('click', () => this.showFilterSetupPrompt());
    this.container.querySelector('#btn-banner-set-filter')?.addEventListener('click', () => this.showFilterSetupPrompt());

    // Empty state "Show All" button
    this.container.querySelector('#btn-empty-show-all')?.addEventListener('click', () => {
      this.applyUserPreferencesFilter = false;
      if (this.currentTab === 'assign') {
        this.loadActivePatients(1);
      } else {
        this.render();
      }
    });

    // Theme toggle
    this.container.querySelector('#btn-theme-toggle')?.addEventListener('click', () => {
      const newTheme = toggleTheme();
      appLogger.info(`Switched interface theme to ${newTheme.toUpperCase()} mode.`);
      const themeBtn = this.container.querySelector('#btn-theme-toggle');
      if (themeBtn) {
        themeBtn.setAttribute('title', newTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode');
        themeBtn.innerHTML = newTheme === 'dark'
          ? `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`
          : `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`;
      }
    });

    // Refresh
    this.container.querySelector('#btn-refresh')?.addEventListener('click', () => {
      if (this.currentTab === 'acknowledge') {
        this.loadHandovers();
      } else {
        this.loadActivePatients(this.activePatientsPage);
      }
    });

    // Accept / Acknowledge All (Top and Bottom)
    this.container.querySelector('#btn-accept-all')?.addEventListener('click', () => this.openBatchAcceptModal());
    this.container.querySelector('#btn-acknowledge-all-bottom')?.addEventListener('click', () => this.openBatchAcceptModal());

    // Logout
    this.container.querySelector('#btn-logout')?.addEventListener('click', () => {
      this.destroy();
      userService.stopHeartbeat();
      api.clearSession();
      appLogger.info('Logged out from desk.');
      this.onLogout();
    });

    // Tab: Acknowledge (and stat unit click)
    const handleAcknowledgeSwitch = () => {
      if (this.currentTab !== 'acknowledge') {
        this.currentTab = 'acknowledge';
        this.render();
      }
    };
    this.container.querySelector('#tab-acknowledge')?.addEventListener('click', handleAcknowledgeSwitch);
    this.container.querySelector('#stat-unit-pending')?.addEventListener('click', handleAcknowledgeSwitch);

    // Tab: Assign (and stat unit click)
    const handleAssignSwitch = () => {
      if (this.currentTab !== 'assign') {
        this.currentTab = 'assign';
        if (this.activePatients.length === 0) {
          this.loadActivePatients(1);
        } else {
          this.render();
        }
      }
    };
    this.container.querySelector('#tab-assign')?.addEventListener('click', handleAssignSwitch);
    this.container.querySelector('#stat-unit-inpatients')?.addEventListener('click', handleAssignSwitch);

    // Search
    const searchInput = this.container.querySelector('#search-input') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      if (this.searchDebounceTimer !== null) {
        window.clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = window.setTimeout(() => {
        this.searchDebounceTimer = null;
        if (this.currentTab === 'assign') {
          this.loadActivePatients(1);
        } else {
          window.requestAnimationFrame(() => {
            this.refreshCurrentList();
          });
        }
      }, 250);
    });

    this.container.querySelector('#btn-clear-search')?.addEventListener('click', () => {
      this.searchQuery = '';
      if (this.searchDebounceTimer !== null) {
        window.clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
      if (searchInput) {
        searchInput.value = '';
        searchInput.focus();
      }
      if (this.currentTab === 'assign') {
        this.loadActivePatients(1);
      } else {
        this.refreshCurrentList();
      }
    });

    // Assign tab: Nurse picker events
    this.container.querySelector('#btn-open-assign-nurse')?.addEventListener('click', () => {
      this.showAssignNursePicker = true;
      this.render();
      this.loadInitialNurses();
      // Focus the search input
      setTimeout(() => {
        const inp = this.container.querySelector('#assign-nurse-search') as HTMLInputElement;
        if (inp) inp.focus();
      }, 100);
    });

    this.container.querySelector('#btn-change-assign-nurse')?.addEventListener('click', () => {
      this.showAssignNursePicker = true;
      this.render();
      this.loadInitialNurses();
      setTimeout(() => {
        const inp = this.container.querySelector('#assign-nurse-search') as HTMLInputElement;
        if (inp) inp.focus();
      }, 100);
    });

    // Nurse search within picker
    const nurseSearchInput = this.container.querySelector('#assign-nurse-search') as HTMLInputElement;
    nurseSearchInput?.addEventListener('input', (e) => {
      const val = (e.target as HTMLInputElement).value;
      if (this.assignNurseDebounceTimer !== null) {
        window.clearTimeout(this.assignNurseDebounceTimer);
      }
      this.assignNurseDebounceTimer = window.setTimeout(() => {
        this.assignNurseDebounceTimer = null;
        this.searchAssignNurses(val);
      }, 250);
    });

    // Pagination
    this.container.querySelector('#btn-active-prev-page')?.addEventListener('click', () => {
      if (this.activePatientsPage > 1 && !this.isLoadingActivePatients) {
        this.loadActivePatients(this.activePatientsPage - 1);
      }
    });

    this.container.querySelector('#btn-active-next-page')?.addEventListener('click', () => {
      if (this.activePatientsPage < this.activePatientsTotalPages && !this.isLoadingActivePatients) {
        this.loadActivePatients(this.activePatientsPage + 1);
      }
    });

    // Toggle Select All (Assign tab)
    this.container.querySelector('#btn-toggle-select-all')?.addEventListener('click', () => {
      const activeFiltered = this.getFilteredActivePatients();
      const allSelected = this.selectedAssignPatientKeys.size === activeFiltered.length && activeFiltered.length > 0;
      if (allSelected) {
        this.selectedAssignPatientKeys.clear();
      } else {
        activeFiltered.forEach((p) => {
          this.selectedAssignPatientKeys.add(`${p.hospitalNumber}-${p.ipNumber}`);
        });
      }
      this.refreshCurrentList();
      this.updateFloatingBatchBar();
    });

    // Nurse Picker Modal Close & Search Clear
    this.container.querySelector('#btn-close-nurse-picker')?.addEventListener('click', () => {
      this.showAssignNursePicker = false;
      this.render();
    });

    this.container.querySelector('#nurse-picker-overlay')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'nurse-picker-overlay') {
        this.showAssignNursePicker = false;
        this.render();
      }
    });

    this.container.querySelector('#btn-clear-nurse-search')?.addEventListener('click', () => {
      this.assignNurseSearchQuery = '';
      const inp = this.container.querySelector('#assign-nurse-search') as HTMLInputElement;
      if (inp) {
        inp.value = '';
        inp.focus();
      }
      this.searchAssignNurses('');
    });

    this.attachCardActions();
    if (this.currentTab === 'assign') {
      this.updateFloatingBatchBar();
    }
  }

  private refreshCurrentList() {
    if (this.currentTab === 'acknowledge') {
      const queueFiltered = this.getFilteredQueuePatients();
      const listContainer = this.container.querySelector('#patient-list-container');
      const badge = this.container.querySelector('#tab-acknowledge .segmented-badge');
      if (badge) badge.textContent = String(queueFiltered.length);
      if (listContainer) {
        if (queueFiltered.length === 0) {
          listContainer.innerHTML = `
            <div class="empty-apple-state">
              <h3>No Matching Patients</h3>
              <p>No records match your search query.</p>
            </div>
          `;
        } else {
          listContainer.innerHTML = queueFiltered.map((p, idx) => this.renderAcknowledgeCard(p, idx)).join('');
          this.attachCardActions();
        }
      }

      // Update top and bottom Acknowledge All buttons
      const bottomBar = this.container.querySelector('#acknowledge-bottom-bar') as HTMLElement | null;
      if (bottomBar) {
        if (queueFiltered.length === 0) {
          bottomBar.style.display = 'none';
        } else {
          bottomBar.style.display = 'block';
          const countBadge = bottomBar.querySelector('#bottom-ack-count');
          if (countBadge) {
            countBadge.textContent = String(queueFiltered.length);
          }
          const textSpan = bottomBar.querySelector('.acknowledge-glass-text');
          if (textSpan) {
            textSpan.textContent = `Pending handover${queueFiltered.length === 1 ? '' : 's'}`;
          }
        }
      }

      const topBtn = this.container.querySelector('#btn-accept-all');
      if (topBtn) {
        const topBtnSpan = topBtn.querySelector('span');
        if (topBtnSpan) {
          topBtnSpan.textContent = `Acknowledge All (${queueFiltered.length})`;
        }
        (topBtn as HTMLButtonElement).disabled = queueFiltered.length === 0;
      }

      // Keep pending stat card value in sync
      const pendingStatVal = this.container.querySelector('#stat-unit-pending .stat-unit-value');
      if (pendingStatVal) {
        pendingStatVal.textContent = String(queueFiltered.length);
      }
    } else {
      const activeFiltered = this.getFilteredActivePatients();
      const listContainer = this.container.querySelector('#active-patient-list-container');
      const badge = this.container.querySelector('#tab-assign .segmented-badge');
      if (badge) badge.textContent = String(activeFiltered.length);
      if (listContainer) {
        if (activeFiltered.length === 0) {
          listContainer.innerHTML = `
            <div class="empty-apple-state">
              <h3>No Matching Inpatients</h3>
              <p>No records match your search query.</p>
            </div>
          `;
        } else {
          listContainer.innerHTML = activeFiltered.map((p, idx) => this.renderAssignCard(p, idx)).join('');
          this.attachCardActions();
          this.updateFloatingBatchBar();
        }
      }
    }
  }

  private attachCardActions() {
    // 1. Single Accept buttons (Acknowledge tab)
    const acceptBtns = this.container.querySelectorAll('.btn-accept-single');
    acceptBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const idx = Number((e.currentTarget as HTMLElement).getAttribute('data-index'));
        const patient = this.getFilteredQueuePatients()[idx];
        if (!patient) return;

        const modalMount = this.container.querySelector('#dashboard-modal-container') as HTMLElement;
        if (modalMount) {
          new BatchAcceptModal(modalMount, [patient], () => {
            this.loadHandovers();
          });
        }
      });
    });

    // 2. Assign/Give Handover buttons (Assign tab)
    const giveActiveBtns = this.container.querySelectorAll('.btn-give-handover-active');
    giveActiveBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const idx = Number((e.currentTarget as HTMLElement).getAttribute('data-index'));
        const patient = this.getFilteredActivePatients()[idx];
        if (!patient) return;

        if (!this.assignTargetNurse) {
          // Open nurse picker
          this.showAssignNursePicker = true;
          this.render();
          this.loadInitialNurses();
          return;
        }

        // Direct assign via GiveHandoverModal with pre-selected nurse
        this.openGiveHandoverModal(patient, this.assignTargetNurse);
      });
    });

    // 3. Click patient card to toggle selection (Assign tab)
    const assignCards = this.container.querySelectorAll('.assign-patient-card');
    assignCards.forEach((card) => {
      card.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.btn-patient-action') || target.closest('.btn-give-handover-active') || target.closest('.meta-assigned')) {
          return;
        }
        const key = card.getAttribute('data-key');
        if (!key) return;

        if (this.selectedAssignPatientKeys.has(key)) {
          this.selectedAssignPatientKeys.delete(key);
          card.classList.remove('is-selected');
        } else {
          this.selectedAssignPatientKeys.add(key);
          card.classList.add('is-selected');
        }
        this.updateFloatingBatchBar();
      });
    });
  }

  private updateFloatingBatchBar() {
    let bar = this.container.querySelector('#assign-floating-bar') as HTMLElement;
    const count = this.selectedAssignPatientKeys.size;
    const selectAllBtn = this.container.querySelector('#btn-toggle-select-all');
    const activeFiltered = this.getFilteredActivePatients();

    if (selectAllBtn) {
      selectAllBtn.textContent = count === activeFiltered.length && activeFiltered.length > 0 ? 'Deselect All' : 'Select All';
    }

    if (count === 0 || this.currentTab !== 'assign') {
      if (bar) bar.remove();
      return;
    }

    const nurseFullName = this.assignTargetNurse ? this.assignTargetNurse.userName : 'Nurse';
    const nurseFirstName = this.assignTargetNurse
      ? this.assignTargetNurse.userName.trim().split(/\s+/)[0]
      : 'Nurse';
    const html = `
      <div class="floating-bar-content">
        <div class="floating-bar-info">
          <span class="floating-count-badge">${count}</span>
          <span class="floating-text">${count > 1 ? 's' : ''} selected</span>
        </div>
        <div class="floating-bar-actions">
          <button class="btn btn-secondary btn-pill floating-clear-btn" id="btn-clear-selection">
            Clear
          </button>
          <button class="btn btn-primary btn-pill floating-assign-btn" id="btn-batch-assign-confirm" ${this.isBatchAssigning ? 'disabled' : ''} title="${this.assignTargetNurse ? `Assign to ${nurseFullName}` : 'Assign to Nurse'}">
            ${this.isBatchAssigning ? `
              <div class="spinner" style="width: 14px; height: 14px; border-width: 2px; border-color: #fff #fff transparent transparent;"></div>
              <span>Assigning...</span>
            ` : `
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <polyline points="16 11 18 13 22 9"/>
              </svg>
              <span class="floating-assign-label">Assign to <span class="floating-nurse-name" title="${nurseFullName}">${nurseFirstName}</span></span>
            `}
          </button>
        </div>
      </div>
    `;

    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'assign-floating-bar';
      bar.className = 'assign-floating-bar animate-slide-up';
      this.container.appendChild(bar);
    }
    bar.innerHTML = html;

    // Attach listeners
    bar.querySelector('#btn-clear-selection')?.addEventListener('click', () => {
      this.selectedAssignPatientKeys.clear();
      this.container.querySelectorAll('.assign-patient-card').forEach((card) => card.classList.remove('is-selected'));
      this.updateFloatingBatchBar();
    });

    bar.querySelector('#btn-batch-assign-confirm')?.addEventListener('click', () => {
      this.executeBatchAssign();
    });
  }

  private async executeBatchAssign() {
    if (!this.assignTargetNurse) {
      this.showAssignNursePicker = true;
      this.render();
      this.loadInitialNurses();
      return;
    }

    const patientsToAssign = this.getFilteredActivePatients().filter((p) => {
      const key = `${p.hospitalNumber}-${p.ipNumber}`;
      return this.selectedAssignPatientKeys.has(key);
    });

    if (patientsToAssign.length === 0) return;

    const count = patientsToAssign.length;
    const nurse = this.assignTargetNurse;
    const targetEmpId = nurse.employeeId || nurse.empId || nurse.id;

    if (!confirm(`Assign care of ${count} selected patient(s) to ${nurse.userName}?`)) {
      return;
    }

    this.isBatchAssigning = true;
    this.updateFloatingBatchBar();

    let successCount = 0;
    let failCount = 0;

    for (const patient of patientsToAssign) {
      try {
        const res = await api.giveHandover({
          hospitalNumber: patient.hospitalNumber,
          ipNumber: patient.ipNumber,
          targetNurseLoginId: nurse.loginId,
          targetNurseName: nurse.userName,
          targetNurseEmpId: targetEmpId,
          targetNurse: nurse,
          remarks: 'Shift handover batch transfer',
          patient,
        });
        if (res.success) {
          successCount++;
          handoverTrackingService.recordAssignment({
            hospitalNumber: patient.hospitalNumber,
            ipNumber: patient.ipNumber,
            targetNurseLoginId: nurse.loginId,
            targetNurseName: nurse.userName,
            targetNurseEmpId: targetEmpId,
            assignedByLoginId: api.loginId || '',
            assignedByName: api.getUserDisplayName(),
            assignedAt: Date.now(),
            reportKey: res.reportKey,
            ackStatus: 'NTC0001',
            verified: true,
          });
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    // Background verify assignments via doctor-desk-mobile/doctor-visit-details
    setTimeout(() => {
      patientsToAssign.forEach((p) => {
        handoverTrackingService.verifyAssignment(p.hospitalNumber, p.ipNumber).catch(() => { });
      });
    }, 1500);

    this.isBatchAssigning = false;
    this.selectedAssignPatientKeys.clear();

    if (failCount === 0) {
      appLogger.success(`Successfully assigned ${successCount} patient(s) to ${nurse.userName}.`);
    } else {
      appLogger.warn(`Assigned ${successCount} patient(s), ${failCount} failed.`);
    }

    this.loadHandovers();
    this.loadActivePatients(this.activePatientsPage);
  }

  private openBatchAcceptModal() {
    const modalMount = this.container.querySelector('#dashboard-modal-container') as HTMLElement;
    const arrivedPatients = this.getFilteredQueuePatients();
    if (modalMount && arrivedPatients.length > 0) {
      new BatchAcceptModal(modalMount, arrivedPatients, () => {
        this.loadHandovers();
      });
    }
  }

  private openGiveHandoverModal(patient: PatientHandoverItem | ActiveInpatient, targetNurse?: NurseUser | null) {
    const modalMount = this.container.querySelector('#dashboard-modal-container') as HTMLElement;
    if (modalMount) {
      new GiveHandoverModal(
        modalMount,
        patient,
        (assignedNurse) => {
          if (assignedNurse && (!this.assignTargetNurse || this.assignTargetNurse.loginId !== assignedNurse.loginId)) {
            this.assignTargetNurse = assignedNurse;
            this.preferredHandoverNurse = assignedNurse;
          }
          this.loadHandovers();
          if (this.currentTab === 'assign') {
            this.loadActivePatients(this.activePatientsPage);
          }
        },
        () => {
          modalMount.innerHTML = '';
        },
        targetNurse || this.assignTargetNurse
      );
    }
  }

  public destroy() {
    userService.stopHeartbeat();
    if (this.searchDebounceTimer !== null) {
      window.clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    if (this.assignNurseDebounceTimer !== null) {
      window.clearTimeout(this.assignNurseDebounceTimer);
      this.assignNurseDebounceTimer = null;
    }
  }
}
